import type React from 'react';
import { memo, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { PORTAL_ROOT_ID } from '@utils/portal';
import { KawaiiButton } from './button';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void; // Keep this prop!
  children: React.ReactNode;
  title?: string;
  size?: 'sm' | 'md' | 'lg';
}

const getSizeClassName = (size: ModalProps['size']) => {
  switch (size) {
    case 'sm':
      return 'min-w-[300px] max-w-[500px]';
    case 'lg':
      return 'min-w-[900px] max-w-[1100px]';
    default:
      return 'min-w-[600px] max-w-[800px]';
  }
};

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.3 } },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

const fancyModalVariants = {
  hidden: {
    opacity: 0,
    scale: 0.6,
    rotate: -15,
    y: 100,
  },
  visible: {
    opacity: 1,
    scale: 1,
    rotate: 0,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 280,
      damping: 20,
      mass: 0.8,
      delay: 0.1,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.7,
    rotate: 10,
    y: 80,
    transition: {
      duration: 0.25,
      ease: 'easeIn',
    },
  },
};

const Modal = ({ isOpen, onClose, children, title, size }: ModalProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null);

  // Effect to handle showing the modal and body scroll lock
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen) {
      // Only show modal if it's not already open natively
      if (!dialog.open) {
        dialog.showModal();
        document.body.style.overflow = 'hidden';
      }
    } else {
      // If isOpen becomes false, we rely on animation/native close,
      // but ensure scroll is unlocked if it was open.
      // We don't call dialog.close() here directly anymore.
      if (dialog.open) {
        document.body.style.overflow = 'unset';
      }
    }

    // Cleanup scroll lock on unmount
    return () => {
      // Ensure scroll is unlocked if component unmounts while modal was open
      // Check document state directly as component state might be stale
      if (document.body.style.overflow === 'hidden') {
        document.body.style.overflow = 'unset';
      }
    };
  }, [isOpen]); // Rerun when isOpen changes

  // Function to close the native dialog AFTER animation completes
  const handleAnimationComplete = () => {
    // Only close if the state is 'closed' AND the dialog element still exists AND is natively open
    // This prevents errors if Escape already closed it natively.
    if (!isOpen && dialogRef.current?.open) {
      dialogRef.current.close();
      // Ensure scroll is unlocked after closing, just in case
      document.body.style.overflow = 'unset';
    }
  };

  // NEW: Handler for the native dialog's close event (triggered by Escape)
  const handleNativeDialogClose = () => {
    // If the native dialog closes (e.g., via Escape),
    // ensure our React state reflects this by calling the passed onClose prop.
    // This will set isOpen to false and trigger the exit animation.
    onClose();
  };

  return (
    // AnimatePresence handles the mounting/unmounting based on isOpen
    <AnimatePresence mode="wait" onExitComplete={handleAnimationComplete}>
      {isOpen && (
        <dialog
          ref={dialogRef}
          onClose={handleNativeDialogClose} // ADDED: Handle native close (Escape key)
          className="bg-transparent p-0 m-auto border-none overflow-visible"
          aria-labelledby={title ? 'modal-title' : undefined}
          aria-modal="true"
        >
          {/* Animated Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-xs z-40"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose} // Click backdrop calls our onClose prop
          />

          {/* Animated Modal Content Container */}
          <motion.div
            className={`${getSizeClassName(size)} relative z-50 mx-auto my-8 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-xl overflow-hidden`}
            variants={fancyModalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            // Prevent clicks inside the modal content from bubbling up to the backdrop/dialog
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 p-4">
              {title && (
                <h2
                  id="modal-title"
                  className="text-lg font-semibold text-pink-600 dark:text-pink-300"
                >
                  {title}
                </h2>
              )}
              <KawaiiButton
                onClick={onClose} // Button calls our onClose prop
                variant="secondary"
                className="ml-auto p-2"
                aria-label="Close modal"
              >
                <X size={20} />
              </KawaiiButton>
            </div>
            {/* Body */}
            <div className="p-4 relative text-gray-700 dark:text-gray-300">
              {children}
              <div id={PORTAL_ROOT_ID} />
            </div>
          </motion.div>
        </dialog>
      )}
    </AnimatePresence>
  );
};

export default memo(Modal);
